export type AudioMetrics = {
  frequency: Uint8Array<ArrayBuffer>;
  waveform: Uint8Array<ArrayBuffer>;
  bands: [number, number, number, number, number, number, number, number];
  low: number;
  mid: number;
  high: number;
  rms: number;
  peak: number;
  centroid: number;
};

type PlayerState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

type TrackState = {
  title: string;
  duration: number;
  id: string;
  uploaded: boolean;
};

const EMPTY_TRACK: TrackState = {
  title: 'Choose a track or upload',
  duration: 0,
  id: 'empty',
  uploaded: false,
};

const BAND_RANGES = [
  [20, 60], [60, 150], [150, 400], [400, 1000],
  [1000, 2500], [2500, 6000], [6000, 15000], [15000, 20000],
] as const;

const PLAYBACK_FADE_IN = 0.58;
const PLAYBACK_FADE_OUT = 0.46;
const PREVIEW_FRAME_RATE = 8;
const PREVIEW_FRAME_COUNT = 720;
const PREVIEW_WINDOW_SIZE = 1024;
const PREVIEW_BAND_CENTERS = [35, 95, 245, 632, 1581, 3873, 9487, 17320] as const;

export class AudioEngine extends EventTarget {
  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private playbackGain: GainNode | null = null;
  private master: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private trackBuffer: AudioBuffer | null = null;
  private trackTitle = '';
  private trackId = '';
  private trackUploaded = false;
  private loadGeneration = 0;
  private offset = 0;
  private startedAt = 0;
  private sourceGeneration = 0;
  private muted = false;
  private volume = 0.78;
  private previewing = false;
  private previewStartedAt = 0;
  private previewFrames = new Float32Array(0);
  private previewFrameCount = 0;
  private previewDuration = 0;
  private frequencyData = new Uint8Array(1024);
  private waveformData = new Uint8Array(2048);
  state: PlayerState = 'idle';

  get track(): TrackState {
    if (!this.trackBuffer) return EMPTY_TRACK;
    return {
      title: this.trackTitle,
      duration: this.trackBuffer.duration,
      id: this.trackId,
      uploaded: this.trackUploaded,
    };
  }

  get hasTrack(): boolean {
    return this.trackBuffer !== null;
  }

  get isPreviewing(): boolean {
    return this.previewing;
  }

  get position(): number {
    if (this.previewing && this.previewDuration > 0 && this.trackBuffer) {
      const phase = ((performance.now() - this.previewStartedAt) / 1000) % this.previewDuration;
      return (phase / this.previewDuration) * this.trackBuffer.duration;
    }
    if (!this.context || this.state !== 'playing') return this.offset;
    return Math.min(this.offset + (this.context.currentTime - this.startedAt), this.track.duration);
  }

  get sampleRate(): number {
    return this.context?.sampleRate ?? 0;
  }

  async initialize(): Promise<void> {
    if (this.context) return;
    const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioContextCtor) throw new Error('Web Audio is not supported by this browser.');
    this.context = new AudioContextCtor();
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0;
    this.playbackGain = this.context.createGain();
    this.playbackGain.gain.value = 0;
    this.master = this.context.createGain();
    this.master.gain.value = this.volume;
    this.analyser.connect(this.playbackGain);
    this.playbackGain.connect(this.master);
    this.master.connect(this.context.destination);
    this.refreshAnalysisArrays();
  }

  async toggle(): Promise<void> {
    if (this.state === 'playing') {
      await this.pause();
      return;
    }
    await this.play();
  }

  async play(): Promise<void> {
    if (!this.trackBuffer) throw new Error('Choose a track or upload audio before playback.');
    await this.initialize();
    if (!this.context || !this.analyser) return;
    if (this.context.state === 'suspended') await this.context.resume();

    this.setState('loading');
    try {
      if (this.offset >= this.trackBuffer.duration - 0.02) this.offset = 0;
      this.startSource(this.trackBuffer, this.offset);
      this.previewing = false;
      this.setState('playing');
    } catch (error) {
      this.setState('error');
      throw error;
    }
  }

  async pause(): Promise<void> {
    if (!this.context || this.state !== 'playing') return;
    this.offset = this.position;
    this.setState('paused');
    await this.fadeOutAndStop();
  }

  async loadFile(file: File): Promise<void> {
    await this.initialize();
    if (!this.context) return;
    const generation = ++this.loadGeneration;
    this.offset = 0;
    this.setState('loading');
    try {
      await this.fadeOutAndStop();
      const arrayBuffer = await file.arrayBuffer();
      await this.decodeTrack(arrayBuffer, {
        title: file.name.replace(/\.[^.]+$/, '') || 'Uploaded track',
        id: 'uploaded',
        uploaded: true,
      }, generation);
    } catch (error) {
      if (generation === this.loadGeneration) this.setState('error');
      throw new Error(error instanceof Error ? error.message : 'The selected audio file could not be decoded.');
    }
  }

  async loadTrack(url: string, title: string, id: string): Promise<void> {
    await this.initialize();
    if (!this.context) return;
    const generation = ++this.loadGeneration;
    this.offset = 0;
    this.setState('loading');
    try {
      await this.fadeOutAndStop();
      const response = await fetch(url);
      if (!response.ok) throw new Error(`The track could not be loaded (${response.status}).`);
      await this.decodeTrack(await response.arrayBuffer(), { title, id, uploaded: false }, generation);
    } catch (error) {
      if (generation === this.loadGeneration) this.setState('error');
      throw new Error(error instanceof Error ? error.message : 'The selected track could not be loaded.');
    }
  }

  async seek(seconds: number): Promise<void> {
    if (!this.trackBuffer) return;
    const next = Math.max(0, Math.min(seconds, this.track.duration));
    const wasPlaying = this.state === 'playing';
    this.stopSource();
    this.offset = next;
    if (wasPlaying) await this.play();
    else this.dispatchEvent(new CustomEvent('progress', { detail: this.position }));
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master && this.context) {
      this.master.gain.cancelScheduledValues(this.context.currentTime);
      this.master.gain.setTargetAtTime(this.muted ? 0 : this.volume, this.context.currentTime, 0.025);
    }
    return this.muted;
  }

  setPreviewing(previewing: boolean): void {
    this.previewing = previewing;
    if (previewing) this.previewStartedAt = performance.now();
  }

  getMetrics(): AudioMetrics {
    const analyser = this.analyser;
    if (this.previewing && this.previewFrameCount > 0) return this.getPreviewMetrics();
    if (!analyser || !this.context) return this.emptyMetrics();
    analyser.getByteFrequencyData(this.frequencyData);
    analyser.getByteTimeDomainData(this.waveformData);

    const nyquist = this.context.sampleRate / 2;
    const hzPerBin = nyquist / this.frequencyData.length;
    const band = (minHz: number, maxHz: number): number => {
      const start = Math.max(0, Math.floor(minHz / hzPerBin));
      const end = Math.min(this.frequencyData.length - 1, Math.ceil(maxHz / hzPerBin));
      let total = 0;
      for (let index = start; index <= end; index += 1) total += this.frequencyData[index];
      return total / Math.max(1, end - start + 1) / 255;
    };
    const bands = BAND_RANGES.map(([min, max]) => band(min, max)) as AudioMetrics['bands'];

    let sumSquares = 0;
    let peak = 0;
    for (const value of this.waveformData) {
      const sample = (value - 128) / 128;
      sumSquares += sample * sample;
      peak = Math.max(peak, Math.abs(sample));
    }

    let weighted = 0;
    let magnitude = 0;
    for (let index = 1; index < this.frequencyData.length; index += 1) {
      const value = this.frequencyData[index] / 255;
      weighted += index * hzPerBin * value;
      magnitude += value;
    }

    return {
      frequency: this.frequencyData,
      waveform: this.waveformData,
      bands,
      low: (bands[0] + bands[1] + bands[2]) / 3,
      mid: (bands[3] + bands[4]) / 2,
      high: (bands[5] + bands[6] + bands[7]) / 3,
      rms: Math.sqrt(sumSquares / this.waveformData.length),
      peak,
      centroid: magnitude > 0 ? weighted / magnitude : 0,
    };
  }

  private startSource(buffer: AudioBuffer, offset: number): void {
    if (!this.context || !this.analyser || !this.playbackGain) return;
    this.stopSource();
    const generation = ++this.sourceGeneration;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyser);
    source.onended = () => {
      if (generation !== this.sourceGeneration || this.state !== 'playing') return;
      this.source = null;
      this.offset = 0;
      this.setState('ended');
    };
    source.start(0, Math.min(offset, Math.max(0, buffer.duration - 0.001)));
    this.source = source;
    this.startedAt = this.context.currentTime;

    const now = this.context.currentTime;
    const remaining = Math.max(0, buffer.duration - offset);
    const gain = this.playbackGain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(0, now);
    gain.linearRampToValueAtTime(1, now + Math.min(PLAYBACK_FADE_IN, remaining * 0.35));
    if (remaining > PLAYBACK_FADE_IN + PLAYBACK_FADE_OUT + 0.2) {
      gain.setValueAtTime(1, now + remaining - PLAYBACK_FADE_OUT);
      gain.linearRampToValueAtTime(0, now + remaining);
    }
  }

  private async fadeOutAndStop(): Promise<void> {
    if (!this.context || !this.playbackGain || !this.source) {
      this.stopSource();
      return;
    }
    const generation = this.sourceGeneration;
    const now = this.context.currentTime;
    const gain = this.playbackGain.gain;
    gain.cancelAndHoldAtTime(now);
    gain.linearRampToValueAtTime(0, now + PLAYBACK_FADE_OUT);
    await new Promise<void>((resolve) => window.setTimeout(resolve, PLAYBACK_FADE_OUT * 1000));
    if (generation === this.sourceGeneration) this.stopSource();
  }

  private async decodeTrack(
    arrayBuffer: ArrayBuffer,
    metadata: Pick<TrackState, 'title' | 'id' | 'uploaded'>,
    generation: number,
  ): Promise<void> {
    if (!this.context) return;
    const decoded = await this.context.decodeAudioData(arrayBuffer.slice(0));
    if (!Number.isFinite(decoded.duration) || decoded.duration < 0.2) {
      throw new Error('The selected file does not contain playable audio.');
    }
    if (generation !== this.loadGeneration) return;
    this.stopSource();
    this.trackBuffer = decoded;
    this.trackTitle = metadata.title;
    this.trackId = metadata.id;
    this.trackUploaded = metadata.uploaded;
    this.offset = 0;
    this.preparePreview(decoded);
    this.setState('paused');
    this.dispatchEvent(new CustomEvent('trackchange', { detail: this.track }));
  }

  private preparePreview(buffer: AudioBuffer): void {
    const channel = buffer.getChannelData(0);
    if (!channel.length) {
      this.previewFrames = new Float32Array(0);
      this.previewFrameCount = 0;
      this.previewDuration = 0;
      return;
    }

    const frameCount = Math.min(PREVIEW_FRAME_COUNT, Math.max(120, Math.floor(buffer.duration * PREVIEW_FRAME_RATE)));
    const frameSize = Math.min(PREVIEW_WINDOW_SIZE, channel.length);
    const frameStride = Math.max(1, Math.floor(Math.max(0, channel.length - frameSize) / Math.max(1, frameCount - 1)));
    const raw = new Float32Array(frameCount * PREVIEW_BAND_CENTERS.length);
    const maxima = new Float32Array(PREVIEW_BAND_CENTERS.length);
    const coefficients = PREVIEW_BAND_CENTERS.map((frequency) => {
      const bin = Math.max(1, Math.min(Math.floor(frameSize / 2), Math.round(frameSize * frequency / buffer.sampleRate)));
      return 2 * Math.cos((2 * Math.PI * bin) / frameSize);
    });

    for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
      const start = Math.min(channel.length - frameSize, frameIndex * frameStride);
      for (let bandIndex = 0; bandIndex < coefficients.length; bandIndex += 1) {
        const coefficient = coefficients[bandIndex];
        let stateOne = 0;
        let stateTwo = 0;
        for (let sampleIndex = 0; sampleIndex < frameSize; sampleIndex += 1) {
          const window = 0.5 - 0.5 * Math.cos((2 * Math.PI * sampleIndex) / Math.max(1, frameSize - 1));
          const sample = channel[start + sampleIndex] * window;
          const stateZero = sample + coefficient * stateOne - stateTwo;
          stateTwo = stateOne;
          stateOne = stateZero;
        }
        const energy = Math.sqrt(Math.max(0, stateOne * stateOne + stateTwo * stateTwo - coefficient * stateOne * stateTwo)) / frameSize;
        const rawIndex = frameIndex * coefficients.length + bandIndex;
        raw[rawIndex] = energy;
        maxima[bandIndex] = Math.max(maxima[bandIndex], energy);
      }
    }

    this.previewFrames = raw;
    this.previewFrameCount = frameCount;
    this.previewDuration = frameCount / PREVIEW_FRAME_RATE;
    for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
      for (let bandIndex = 0; bandIndex < coefficients.length; bandIndex += 1) {
        const rawIndex = frameIndex * coefficients.length + bandIndex;
        const normalized = maxima[bandIndex] > 0 ? raw[rawIndex] / maxima[bandIndex] : 0;
        this.previewFrames[rawIndex] = Math.min(1, Math.pow(normalized, 0.62) * 0.92);
      }
    }
    this.previewStartedAt = performance.now();
  }

  private getPreviewMetrics(): AudioMetrics {
    if (this.previewFrameCount === 0 || this.previewDuration <= 0) return this.emptyMetrics();
    const phase = ((performance.now() - this.previewStartedAt) / 1000) % this.previewDuration;
    const framePosition = phase * PREVIEW_FRAME_RATE;
    const firstFrame = Math.floor(framePosition) % this.previewFrameCount;
    const nextFrame = (firstFrame + 1) % this.previewFrameCount;
    const blend = framePosition - Math.floor(framePosition);
    const bands = PREVIEW_BAND_CENTERS.map((_, bandIndex) => {
      const first = this.previewFrames[firstFrame * PREVIEW_BAND_CENTERS.length + bandIndex];
      const next = this.previewFrames[nextFrame * PREVIEW_BAND_CENTERS.length + bandIndex];
      return first + (next - first) * blend;
    }) as AudioMetrics['bands'];
    const nyquist = this.sampleRate > 0 ? this.sampleRate / 2 : 22050;
    const hzPerBin = nyquist / this.frequencyData.length;
    this.frequencyData.fill(0);
    for (let index = 0; index < this.frequencyData.length; index += 1) {
      const frequency = index * hzPerBin;
      const bandIndex = BAND_RANGES.findIndex(([minimum, maximum]) => frequency >= minimum && frequency < maximum);
      const safeBandIndex = bandIndex < 0 ? (frequency < BAND_RANGES[0][0] ? 0 : bands.length - 1) : bandIndex;
      const value = bands[safeBandIndex];
      this.frequencyData[index] = Math.round(value * 255);
    }
    for (let index = 0; index < this.waveformData.length; index += 1) {
      let sample = 0;
      for (let bandIndex = 0; bandIndex < bands.length; bandIndex += 1) {
        sample += bands[bandIndex] * Math.sin((index / this.waveformData.length) * Math.PI * 2 * (bandIndex + 1) + phase * (bandIndex + 1) * 0.4);
      }
      this.waveformData[index] = Math.max(0, Math.min(255, Math.round(128 + sample * 9)));
    }
    const low = (bands[0] + bands[1] + bands[2]) / 3;
    const mid = (bands[3] + bands[4]) / 2;
    const high = (bands[5] + bands[6] + bands[7]) / 3;
    const rms = Math.min(1, (bands.reduce((total, value) => total + value, 0) / bands.length) * 0.82);
    const peak = Math.max(...bands);
    let weighted = 0;
    let magnitude = 0;
    bands.forEach((value, index) => {
      weighted += PREVIEW_BAND_CENTERS[index] * value;
      magnitude += value;
    });
    return {
      frequency: this.frequencyData,
      waveform: this.waveformData,
      bands,
      low,
      mid,
      high,
      rms,
      peak,
      centroid: magnitude > 0 ? weighted / magnitude : 0,
    };
  }

  private stopSource(): void {
    this.sourceGeneration += 1;
    if (!this.source) return;
    this.source.onended = null;
    try { this.source.stop(); } catch { /* already stopped */ }
    this.source.disconnect();
    this.source = null;
  }

  private setState(state: PlayerState): void {
    this.state = state;
    this.dispatchEvent(new CustomEvent('statechange', { detail: state }));
  }

  private refreshAnalysisArrays(): void {
    if (!this.analyser) return;
    this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
    this.waveformData = new Uint8Array(this.analyser.fftSize);
  }

  private emptyMetrics(): AudioMetrics {
    this.frequencyData.fill(0);
    this.waveformData.fill(128);
    return {
      frequency: this.frequencyData,
      waveform: this.waveformData,
      bands: [0, 0, 0, 0, 0, 0, 0, 0],
      low: 0,
      mid: 0,
      high: 0,
      rms: 0,
      peak: 0,
      centroid: 0,
    };
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
